import { MenuItem, TextField } from "@mui/material";
import type {
  DatabricksWorkspaceAuth,
  DatabricksWorkspaceEntry,
} from "./DatabricksWorkspacesIntegrationForm.types";
import { isDatabricksUrl } from "./DatabricksWorkspacesIntegrationForm.utils";

interface DatabricksWorkspacesIntegrationFormProps {
  entry: DatabricksWorkspaceEntry;
  onChange: (entry: DatabricksWorkspaceEntry) => void;
}

export default function DatabricksWorkspacesIntegrationForm({
  entry,
  onChange,
}: DatabricksWorkspacesIntegrationFormProps) {
  const set = (patch: Partial<DatabricksWorkspaceEntry>) =>
    onChange({ ...entry, ...patch });
  const setAuth = (patch: Partial<DatabricksWorkspaceAuth>) =>
    onChange({ ...entry, auth: { ...entry.auth, ...patch } });

  const urlValid = isDatabricksUrl(entry.url);

  return (
    <>
      <p className="m-0 text-sm text-text-secondary">
        Connect a Databricks workspace so Definity can collect its telemetry.
      </p>
      <TextField
        label="Workspace URL"
        placeholder="https://dbc-a1b2345c-d6e7.cloud.databricks.com"
        value={entry.url}
        onChange={(e) => set({ url: e.target.value })}
        error={!!entry.url && !urlValid}
        helperText={entry.url && !urlValid ? "Must start with https://" : undefined}
        fullWidth
        required
        size="small"
      />
      <TextField
        label="Authentication"
        select
        value={entry.auth.auth_type}
        onChange={(e) =>
          setAuth({
            auth_type: e.target.value as DatabricksWorkspaceAuth["auth_type"],
          })
        }
        helperText="OAuth with a service principal is recommended; personal access tokens are supported for legacy setups."
        fullWidth
        size="small"
      >
        <MenuItem value="oauth_m2m">OAuth (service principal)</MenuItem>
        <MenuItem value="pat">Personal access token</MenuItem>
      </TextField>
      {entry.auth.auth_type === "oauth_m2m" ? (
        <>
          <TextField
            label="Client ID"
            placeholder="Service principal application ID"
            value={entry.auth.client_id ?? ""}
            onChange={(e) => setAuth({ client_id: e.target.value })}
            fullWidth
            required
            size="small"
          />
          <TextField
            label="Client secret"
            type="password"
            value={entry.auth.client_secret ?? ""}
            onChange={(e) => setAuth({ client_secret: e.target.value })}
            fullWidth
            required
            size="small"
          />
        </>
      ) : (
        <TextField
          label="Personal access token"
          type="password"
          value={entry.auth.personal_access_token ?? ""}
          onChange={(e) => setAuth({ personal_access_token: e.target.value })}
          fullWidth
          required
          size="small"
        />
      )}
      <TextField
        label="SQL warehouse ID"
        placeholder="e.g. 1a2b3c4d5e6f7a8b"
        value={entry.sql_warehouse_id}
        onChange={(e) => set({ sql_warehouse_id: e.target.value })}
        helperText="Warehouse used to query Unity Catalog system tables."
        fullWidth
        size="small"
      />
    </>
  );
}
